// 2) Third-party libraries
import { uniqWith, isEqual } from "lodash-es";
import { createAction, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

// 3) Local imports
// 3.1 Constants
import { USERS_PAGE } from "src/config/tableAndPageConstants";
import { usersSortByOptions } from "src/constants/usersSortAndFilters";

// 3.3 Project-specific modules
import {
	ChangeUsersRoleRequestPayload,
	GetUsersRequestPayload,
	RemoveUsersRequestPayload,
	User,
	UserPreference,
	UsersState,
} from "./types";
import { PageData } from "../common/types";

// 3.4 Utility functions / types
import { DynamicObject } from "@utils/commonInterfaces";

// 3.5 Redux actions / store
import type { RootState } from "../storeTypes";

const actionPrefix = 'users';

export const initialState: UsersState = {
	loading: false,
	errorMessages: [],
	changeRolesLoading: false,
	data: [],
	pageData: {
		pageSize: USERS_PAGE.pageSize,
		currentPage: 0,
		hasNextPage: false,
	},
	filters: { Name: '', Email: '', Role: [] },
	sortBy: usersSortByOptions[0],
	sortAscending: true,
	tableFilter: undefined,
	userPreference: {},
};

const authSlice = createSlice({
	name: 'users',
	initialState,
	reducers: {
		getUsersRequestAction: (
			state,
			_action: PayloadAction<GetUsersRequestPayload>
		) => {
			state.loading = true;
		},
		getUsersSuccessAction: (
			state,
			action: PayloadAction<{
				data: User[],
				pageData: PageData
			}>
		) => {
			const { data, pageData } = action.payload;

			if (pageData.currentPage === 0) {
				state.data = data;
			} else {
				state.data = uniqWith(
					[...state.data, ...data],
					isEqual
				);
			}

			state.loading = false;
			state.pageData = pageData;
			state.errorMessages = initialState.errorMessages;
		},
		getUsersErrorAction: (
			state,
			action: PayloadAction<string[]>
		) => {
			state.loading = false;
			state.data = [];
			state.errorMessages = action.payload;
		},
		changeUsersRoleRequestAction: (
			state,
			_action: PayloadAction<ChangeUsersRoleRequestPayload>
		) => {
			state.changeRolesLoading = true;
		},
		changeUsersRoleResponseAction: (state) => {
			state.changeRolesLoading = false;
		},
		updateUsersFiltersAction: (
			state,
			action: PayloadAction<GetUsersRequestPayload['filters']>
		) => {
			state.filters = action.payload;
		},
		updateUsersTableFilterAction: (
			state,
			action: PayloadAction<string>
		) => {
			state.tableFilter = action.payload;
		},
		updateUsersSortByAction: (
			state,
			action: PayloadAction<{
				sortBy: GetUsersRequestPayload['sortBy'],
				sortAscending: boolean
			}>
		) => {
			state.sortBy = action.payload.sortBy;
			state.sortAscending = action.payload.sortAscending;
		},
		clearUsersStateAllTableFiltersAction: (state) => {
			state.filters = initialState.filters;
			state.sortBy = initialState.sortBy;
			state.sortAscending = initialState.sortAscending;
			state.tableFilter = initialState.tableFilter;
		},
		clearUsersStateDataAction: (state) => {
			Object.assign(state, initialState);
		},
		getUserPreferencesSuccessAction: (
			state,
			action: PayloadAction<UserPreference>
		) => {
			state.userPreference = action.payload;
		}
	},
});

const { actions, reducer } = authSlice;

// Actions
export const {
	getUsersRequestAction,
	getUsersSuccessAction,
	getUsersErrorAction,
	changeUsersRoleRequestAction,
	changeUsersRoleResponseAction,
	updateUsersFiltersAction,
	updateUsersTableFilterAction,
	updateUsersSortByAction,
	clearUsersStateAllTableFiltersAction,
	clearUsersStateDataAction,
	getUserPreferencesSuccessAction
} = actions;

export const getUserByNameAction = createAction(
	`${actionPrefix}/getUserByNameAction`, 
	function prepare(name: string, callback: (res: DynamicObject) => void)
	{
		return {
			payload: { name, callback },
		};
	}
);

export const getUserByEmailAction = createAction(
	`${actionPrefix}/getUserByEmailAction`,
	function prepare(email: string, callback: (res: DynamicObject) => void)
	{
		return {
			payload: { email, callback },
		};
	}
);

export const removeUsersRequestAction = createAction(
	`${actionPrefix}/removeUsersRequestAction`,
	function prepare(payload: RemoveUsersRequestPayload)
	{
		return {
			payload,
		};
	}
);

export const getUserLastLoginDateAction = createAction(
	`${actionPrefix}/getUserLastLoginDateAction`,
	function prepare(callback: (res: DynamicObject) => void)
	{
		return {
			payload: { callback },
		};
	}
);

export const getUserPreferencesRequestAction = createAction(
	`${actionPrefix}/getUserPreferencesRequestAction`,
	function prepare(callback?: (res: DynamicObject) => void)
	{
		return {
			payload: { callback },
		};
	}
);

export const getUserByNameAndEmailAction = createAction(
	`${actionPrefix}/getUserByNameAndEmailAction`,
	function prepare(
		inputValue: string,
		callback: (res: DynamicObject) => void
	) {
		return {
			payload: {
				inputValue,
				callback
			},
		};
	}
);

// Selectors
export const getUsersState = (state: RootState) => state.users;
export const getUserPreference = createSelector(
	getUsersState,
	(users: UsersState) => users.userPreference
)
// Reducer
export default reducer;